import { COUNTRY_METADATA, getCountryMetadata } from '@/config/domain/countryMetadata';

const FLEX_COUNTRY_CODES = Object.keys(COUNTRY_METADATA).sort();
const DEFAULT_FLEX_COUNTRY = FLEX_COUNTRY_CODES.includes('NL')
  ? 'NL'
  : FLEX_COUNTRY_CODES[0] || null;

const clone = (value) => JSON.parse(JSON.stringify(value));

const normalizeCountryCode = (value) =>
  String(value || '')
    .trim()
    .toUpperCase();

const normalizeCountryCodes = (values = []) =>
  [...new Set((values || []).map((value) => normalizeCountryCode(value)).filter(Boolean))].sort();

const COUNTRY_FILTER_IDS = new Set(['country', 'country_code']);

const toFilterValues = (filter = {}) => {
  const rawValues = filter?.values ?? filter?.allowed_values ?? filter?.options ?? filter?.choices ?? [];
  if (!Array.isArray(rawValues)) {
    return [];
  }

  return rawValues
    .map((entry) => {
      if (entry === null || entry === undefined) return null;
      if (typeof entry === 'string') return entry;
      if (typeof entry === 'object') {
        return entry.value ?? entry.id ?? entry.code ?? entry.key ?? null;
      }
      return String(entry);
    })
    .filter(Boolean);
};

export const extractCountryCodesFromFilters = (filters = []) => {
  const normalizedFilters = Array.isArray(filters) ? filters : [];
  const countryFilter = normalizedFilters.find((filter) => {
    const filterId = String(filter?.id ?? filter?.name ?? filter?.key ?? '').toLowerCase();
    return COUNTRY_FILTER_IDS.has(filterId);
  });

  if (!countryFilter) {
    return [];
  }

  return normalizeCountryCodes(toFilterValues(countryFilter));
};

export const getFlexCountryOptions = () =>
  FLEX_COUNTRY_CODES.map((country) => {
    const metadata = getCountryMetadata(country);
    return {
      value: country,
      label: country,
      labelKey: metadata.labelKey,
      iconSrc: metadata.iconSrc,
    };
  });

export const getDefaultFlexCountry = () => DEFAULT_FLEX_COUNTRY;

export const normalizeFlexCountry = (value) => {
  const candidate = normalizeCountryCode(value);
  if (candidate && FLEX_COUNTRY_CODES.includes(candidate)) {
    return candidate;
  }
  return DEFAULT_FLEX_COUNTRY;
};

export const applyCountryFilterToPanels = (
  panels,
  country,
  { hidePanelFilters = true, includePanelIds = null } = {}
) => {
  const clonedPanels = clone(panels || []);
  if (!country) {
    return clonedPanels;
  }

  const includedIds = Array.isArray(includePanelIds) ? new Set(includePanelIds) : null;

  return clonedPanels.map((panel) => {
    if (panel?.type !== 'datasetChart') {
      return panel;
    }

    if (includedIds && !includedIds.has(panel.id)) {
      return panel;
    }

    const nextControlOverrides = {
      ...(panel.controlOverrides || {}),
      filters: {
        ...(panel.controlOverrides?.filters || {}),
        country,
      },
    };

    if (hidePanelFilters) {
      nextControlOverrides.visibility = {
        ...(panel.controlOverrides?.visibility || {}),
        filters: panel.controlOverrides?.visibility?.filters === true,
      };
    }

    return {
      ...panel,
      controlOverrides: nextControlOverrides,
    };
  });
};
